import { useState, useEffect, useRef } from "react";
import { useApp, api } from "../context";
import { Shell, EmptyState } from "../components";
import { TriviaChallengeCard, TriviaGameBar, TriviaGameModal, TriviaGameOver } from "../TriviaGame";

const ROOM_TYPES = [
  { id: "all",        label: "All",           icon: "🔴", color: "#e53e3e" },
  { id: "chat",       label: "Chat",          icon: "💬", color: "#6c5ce7" },
  { id: "truth_dare", label: "Truth or Dare", icon: "🎮", color: "#e53e3e" },
  { id: "hot_takes",  label: "Hot Takes",     icon: "🔥", color: "#f59e0b" },
  { id: "role_play",  label: "Role Play",     icon: "🎭", color: "#a855f7" },
  { id: "spicy",      label: "Spicy",         icon: "🔞", color: "#e91e8c" },
  { id: "vibe",       label: "Vibe",          icon: "🎵", color: "#10b981" },
];

const PLAN_LABELS = { free: "Free", premium: "Premium ⭐", elite: "Elite 👑" };

const LiveBadge = ({ color = "#e53e3e" }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 4, background: color + "20", borderRadius: 6, padding: "2px 8px" }}>
    <div style={{ width: 6, height: 6, borderRadius: 3, background: color, animation: "rr_pulse 1.5s infinite" }} />
    <span style={{ fontSize: 10, fontWeight: 800, color, letterSpacing: 1 }}>LIVE</span>
    <style>{`@keyframes rr_pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
  </div>
);

const MusicWave = ({ color = "#10b981" }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 2, height: 20 }}>
    {[3,5,8,6,9,4,7,5,8,6,4,7,5,9,6,8,4,6,7,5].map((h, i) => (
      <div key={i} style={{
        width: 2, height: h * 2, borderRadius: 1, background: color, opacity: 0.8,
        animation: `rr_wave ${0.5 + (i % 5) * 0.1}s ${i * 0.05}s ease-in-out infinite alternate`,
      }} />
    ))}
    <style>{`@keyframes rr_wave{from{transform:scaleY(0.3)}to{transform:scaleY(1)}}`}</style>
  </div>
);

const VoteBar = ({ yesPercent = 64 }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 22 }}>
      <div style={{ width: yesPercent + "%", background: "#22c55e", display: "flex", alignItems: "center", paddingLeft: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#fff" }}>👍 {yesPercent}%</span>
      </div>
      <div style={{ flex: 1, background: "#ef4444", display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#fff" }}>👎 {100 - yesPercent}%</span>
      </div>
    </div>
  </div>
);

const TokenDropBanner = () => {
  const [secs, setSecs] = useState(225);
  useEffect(() => {
    const t = setInterval(() => setSecs(p => p > 0 ? p - 1 : 0), 1000);
    return () => clearInterval(t);
  }, []);
  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  if (secs === 0) return null;
  return (
    <div style={{ background: "linear-gradient(135deg,#f59e0b15,#ef444415)", borderRadius: 14, padding: "12px 14px", border: "1px solid #f59e0b30", display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <span style={{ fontSize: 24 }}>🎁</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f0f0fa" }}>Token Drop in {fmt(secs)}</div>
        <div style={{ fontSize: 11, color: "#888" }}>Join any live room for a chance!</div>
      </div>
      <div style={{ background: "#f59e0b", borderRadius: 10, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer" }}>View Drops</div>
    </div>
  );
};

const ExploreTypes = ({ dark, onSelect }) => {
  const types = [
    { id: "hot_takes", icon: "🔥", label: "Hot Takes", desc: "Debate & Vote", stake: 5, color: "#ef4444" },
    { id: "role_play", icon: "🎭", label: "Role Play", desc: "Create a story", stake: 20, color: "#a855f7" },
    { id: "spicy", icon: "🔞", label: "Spicy", desc: "Adults only", stake: 30, color: "#e91e8c", badge: "18+" },
    { id: "vibe", icon: "🎵", label: "Vibe", desc: "Music & Chill", stake: 15, color: "#3b82f6" },
    { id: "truth_dare", icon: "🎮", label: "Truth or Dare", desc: "Play & dare", stake: 10, color: "#10b981" },
    { id: "chat", icon: "💭", label: "Social", desc: "Just chat", stake: 0, color: "#f59e0b" },
  ];
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#f0f0fa", marginBottom: 10 }}>Explore Room Types</div>
      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8, scrollbarWidth: "none" }}>
        {types.map(t => (
          <div key={t.id} onClick={() => onSelect(t.id)} style={{ flexShrink: 0, width: 105, background: dark ? "#1a1a2a" : "#fff", borderRadius: 14, padding: "12px 10px", border: `1px solid ${t.color}25`, cursor: "pointer" }}>
            {t.badge && <div style={{ fontSize: 9, fontWeight: 800, color: t.color, background: t.color + "20", padding: "1px 6px", borderRadius: 6, display: "inline-block", marginBottom: 4 }}>{t.badge}</div>}
            <div style={{ fontSize: 22, marginBottom: 4 }}>{t.icon}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: dark ? "#f0f0fa" : "#111" }}>{t.label}</div>
            <div style={{ fontSize: 10, color: "#888", marginBottom: 4 }}>{t.desc}</div>
            <div style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>{t.stake === 0 ? "Free" : `${t.stake} GT`}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function RedRoomPage() {
  const { c, dark, user, flash, go } = useApp();
  const [view, setView] = useState("browse");
  const [rooms, setRooms] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState("");
  const [joiningId, setJoiningId] = useState(null);
  const [myRooms, setMyRooms] = useState([]);

  const [createName, setCreateName] = useState("");
  const [createType, setCreateType] = useState("chat");
  const [createVisibility, setCreateVisibility] = useState("public");
  const [createCapacity, setCreateCapacity] = useState(15);
  const [createDuration, setCreateDuration] = useState(2);
const [creating, setCreating] = useState(false);

  const [activeRoom, setActiveRoom] = useState(null);
  const [myAlias, setMyAlias] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);
  const msgEndRef = useRef(null);
  const fileRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [typing, setTyping] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showTip, setShowTip] = useState(false); // { id, alias, content, type, image_url }
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isAtBottomRef = useRef(true);
  const messagesContainerRef = useRef(null);
  const lastSeenMsgIdRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const [hideInput, setHideInput] = useState(false);
  const lastScrollTopRef = useRef(0);
  const [activeGame, setActiveGame] = useState(null);
  const [showGameModal, setShowGameModal] = useState(false);
  const [gameOver, setGameOver] = useState(null);
  const [showTriviaChallenge, setShowTriviaChallenge] = useState(false);
  const [triviaBet, setTriviaBet] = useState(100);
  const [freshGtBalance, setFreshGtBalance] = useState(null);

  const bg = dark ? "#080810" : "#f4f5f9";
  const card = dark ? "#0f0f1a" : "#ffffff";
  const border = dark ? "#ffffff12" : "#e0e2eb";
  const text = dark ? "#ffffff" : "#1a1c2e";
  const sub = dark ? "#ffffff60" : "#6b7094";
  const faint = dark ? "#ffffff30" : "#9ca0b8";
  const input = dark ? "#111120" : "#f0f1f6";

  useEffect(() => { loadRooms(); loadMyRooms(); }, [filterType]);

  useEffect(() => {
    if (activeRoom) {
      loadMessages(activeRoom.id);
      const iv = setInterval(() => {
        loadMessages(activeRoom.id);
        loadTyping(activeRoom.id);
      }, 3000);
      setPollInterval(iv);
      return () => clearInterval(iv);
    }
  }, [activeRoom?.id]);

  useEffect(() => {
    if (!messages.length) return;
    const latestId = messages[messages.length - 1]?.id;
    const newCount = messages.length - prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;

    if (isAtBottomRef.current) {
      msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setUnreadCount(0);
      lastSeenMsgIdRef.current = latestId;
    } else {
      // Only count truly new messages (not reloads of same data)
      if (newCount > 0 && latestId !== lastSeenMsgIdRef.current) {
        const realNew = messages.slice(-newCount).filter(m => !m.is_mine).length;
        if (realNew > 0) {
          setUnreadCount(prev => prev + realNew);
          setShowScrollBtn(true);
        }
      }
    }
  }, [messages]);

  const handleScroll = (e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    const scrollingDown = el.scrollTop > lastScrollTopRef.current;
    lastScrollTopRef.current = el.scrollTop;

    isAtBottomRef.current = atBottom;

    

    setShowScrollBtn(!atBottom);
    if (atBottom) {
      setUnreadCount(0);
      setShowScrollBtn(false);
      lastSeenMsgIdRef.current = messages[messages.length - 1]?.id;
    }
  };

  const scrollToBottom = () => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setUnreadCount(0);
    setShowScrollBtn(false);
    isAtBottomRef.current = true;
    lastSeenMsgIdRef.current = messages[messages.length - 1]?.id;
  };

  const loadRooms = async () => {
    setLoading(true);
    const q = filterType !== "all" ? `?type=${filterType}` : "";
    const r = await api(`/red-rooms${q}`);
    if (r.ok) setRooms(r.data.rooms || []);
    setLoading(false);
  };

  const loadMyRooms = async () => {
    const r = await api("/red-rooms/my");
    if (r.ok) setMyRooms(r.data.hosting || []);
  };

  const loadMessages = async (id) => {
    const r = await api(`/red-rooms/${id}/messages`);
    if (r.ok) setMessages(r.data.messages || []);
  };

  const loadTyping = async (id) => {
    const r = await api(`/red-rooms/${id}/typing`);
    if (r.ok) setTyping(r.data.typing || []);
  };

  const handleJoin = async (roomId) => {
    setJoiningId(roomId);
    const r = await api("/red-rooms/join", { method: "POST", body: JSON.stringify({ room_id: roomId }) });
    setJoiningId(null);
    if (r.ok) {
      setActiveRoom(r.data.room);
      setMyAlias(r.data.alias);
      setView("room");
      flash(`Joined as ${r.data.alias.emoji} ${r.data.alias.name}!`);
      if (r.data.is_first_room) {
        setTimeout(() => {
          setMessages(prev => [{
            id: "welcome",
            type: "system",
            content: `Welcome to Red Room! 🔴 You are completely anonymous here. Your alias is ${r.data.alias.emoji} ${r.data.alias.name}. Be respectful, have fun, and remember — what happens in Red Room, stays in Red Room 😏`,
            created_at: new Date().toISOString(),
          }, ...prev]);
        }, 1000);
      } else {
        setTimeout(() => setShowTip(true), 2000);
        setTimeout(() => setShowTip(false), 12000);
      }
    }
    else flash(r.message || "Failed to join", "error");
  };

  const handleJoinByCode = async () => {
    if (!joinCode.trim()) return;
    const r = await api("/red-rooms/join", { method: "POST", body: JSON.stringify({ code: joinCode.trim().toUpperCase() }) });
    if (r.ok) { setActiveRoom(r.data.room); setMyAlias(r.data.alias); setView("room"); setJoinCode(""); flash(`Joined as ${r.data.alias.emoji} ${r.data.alias.name}!`); }
    else flash(r.message || "Invalid code", "error");
  };

  const handleCreate = async () => {
    if (!createName.trim()) { flash("Enter a room name", "error"); return; }
    setCreating(true);
    const r = await api("/red-rooms", { method: "POST", body: JSON.stringify({ name: createName, type: createType, visibility: createVisibility, capacity: createCapacity, duration_hours: createDuration }) });
    setCreating(false);
    if (r.ok) { setActiveRoom(r.data.room); setMyAlias(r.data.alias); setView("room"); flash(`Room created! Code: ${r.data.room.code}`); }
    else flash(r.message || "Failed to create", "error");
  };

  const handleSend = async () => {
    if (!msgText.trim() && !imageFile) return;
    setSending(true);
    setTyping([]);
    if (imageFile) {
      const fd = new FormData();
      if (msgText.trim()) fd.append("content", msgText.trim());
      fd.append("image", imageFile);
      fd.append("type", "image");
      if (replyingTo?.id) fd.append("reply_to_id", replyingTo.id);
      try {
        const token = localStorage.getItem("gc_token");
        const res = await fetch(`/api/red-rooms/${activeRoom.id}/messages`, { method: "POST", headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }, body: fd });
        const data = await res.json();
        if (res.ok) { setMessages(prev => [...prev, data.message]); setMsgText(""); setImageFile(null); setImagePreview(null); setReplyingTo(null); }
        else flash(data.message || "Failed", "error");
      } catch { flash("Failed to send", "error"); }
    } else {
      const r = await api(`/red-rooms/${activeRoom.id}/messages`, { method: "POST", body: JSON.stringify({ content: msgText.trim(), type: "text", reply_to_id: replyingTo?.id || null }) });
      if (r.ok) { setMessages(prev => [...prev, r.data.message]); setMsgText(""); setReplyingTo(null); }
      else flash(r.message || "Failed", "error");
    }
    setSending(false);
  };

  const handleLongPress = (msg) => {
    if (msg.type === "system") return;
    setReplyingTo({ id: msg.id, alias: msg.alias, content: msg.content, type: msg.type, image_url: msg.image_url });
  };

  const handleReact = async (msgId, emoji) => {
    await api(`/red-rooms/${activeRoom.id}/react`, { method: "POST", body: JSON.stringify({ message_id: msgId, emoji }) });
    loadMessages(activeRoom.id);
  };

  const handleLeave = async () => {
    if (pollInterval) clearInterval(pollInterval);
    await api(`/red-rooms/${activeRoom.id}/leave`, { method: "POST" });
    setActiveRoom(null); setMyAlias(null); setMessages([]); setView("browse");
    loadRooms(); loadMyRooms(); flash("Left the room");
  };

  const handleClose = async () => {
    if (pollInterval) clearInterval(pollInterval);
    const r = await api(`/red-rooms/${activeRoom.id}/close`, { method: "POST" });
    if (r.ok) { flash(`Room closed! You earned ${r.data.gt_earned} GT 🪙`); setActiveRoom(null); setMyAlias(null); setMessages([]); setView("browse"); loadRooms(); loadMyRooms(); }
  };

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { flash("Max 5MB", "error"); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const timeLeft = (closesAt) => {
    if (!closesAt) return "";
    const diff = new Date(closesAt) - new Date();
    if (diff <= 0) return "Expired";
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const isHost = activeRoom && user && activeRoom.host_name === user.name;

  // ── ROOM VIEW ──
  if (view === "room" && activeRoom) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100dvh", background: bg, fontFamily: "'DM Sans',sans-serif", position: "relative" }}>
        {/* Top bar */}
        <div style={{ background: dark ? "#0d0d14" : "#fff", borderBottom: `1px solid ${border}`, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <button onClick={handleLeave} style={{ background: "none", border: "none", color: "#e53e3e", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>← Leave</button>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: text }}>{activeRoom.type_icon} {activeRoom.name}</div>
            <div style={{ fontSize: 10, color: sub }}>{activeRoom.current_count}/{activeRoom.max_capacity} · {timeLeft(activeRoom.closes_at)} left</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {isHost && (
              <button onClick={handleClose} style={{ padding: "4px 10px", borderRadius: 8, border: "1px solid #e53e3e40", background: "#e53e3e15", color: "#e53e3e", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Close</button>
            )}
            <button onClick={() => setShowMembers(!showMembers)} style={{ background: "none", border: "none", color: sub, fontSize: 18, cursor: "pointer" }}>👥</button>
          </div>
        </div>

        {/* Alias bar */}
        {myAlias && (
          <div style={{ background: dark ? "#1a1a2a" : "#f3f0ff", padding: "6px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: sub }}>You are anonymous in this room</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6c5ce7" }}>{myAlias.emoji} {myAlias.name}</div>
          </div>
        )}

        {/* Host code bar */}
        {isHost && (
          <div style={{ background: dark ? "#1a0a0a" : "#fff5f5", borderBottom: `1px solid #e53e3e20`, padding: "6px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: "#e53e3e80" }}>Share this code</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#e53e3e", letterSpacing: 2 }}>{activeRoom.code}</div>
          </div>
        )}

        {/* Trivia Game Bar */}
        {activeGame && !showGameModal && (
          <TriviaGameBar game={activeGame} dark={dark} onExpand={() => setShowGameModal(true)} />
        )}

        {/* Messages */}
        <div ref={messagesContainerRef} onScroll={handleScroll} style={{ flex: 1, overflowY: "auto", padding: "12px 16px", position: "relative", minHeight: 0 }}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: faint, fontSize: 13 }}>No messages yet. Say hello! 👋</div>
          )}
          {messages.map((msg) => {
            if (msg.type === "system") return (
              <div key={msg.id} style={{ textAlign: "center", margin: "8px 0", fontSize: 11, color: faint }}>{msg.content}</div>
            );
            if (msg.type === "game_challenge") return (
              <div key={msg.id} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, color: faint, marginBottom: 3, textAlign: "center" }}>{msg.alias} challenged the room!</div>
                <TriviaChallengeCard
                  msg={msg}
                  myAlias={myAlias?.name}
                  dark={dark}
                  flash={flash}
                  activeRoom={activeRoom}
                  onGameStart={(game) => { setActiveGame(game); setShowGameModal(true); }}
                />
              </div>
            );
            const isMine = msg.is_mine;
            return (
              <div key={msg.id} id={`rrmsg-${msg.id}`} style={{ marginBottom: 14, display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start" }}
                onTouchStart={(e) => { const t = setTimeout(() => handleLongPress(msg), 500); e.currentTarget._pressTimer = t; }}
                onTouchEnd={(e) => { clearTimeout(e.currentTarget._pressTimer); }}
                onTouchMove={(e) => { clearTimeout(e.currentTarget._pressTimer); }}
                onContextMenu={(e) => { e.preventDefault(); handleLongPress(msg); }}
              >
                <div style={{ fontSize: 10, color: faint, marginBottom: 3 }}>{isMine ? `You (${myAlias?.name})` : msg.alias}</div>
                {msg.type === "image" && msg.image_url ? (
                  <div style={{ background: card, borderRadius: 14, overflow: "hidden", border: `1px solid ${border}`, maxWidth: 220 }}>
                    {msg.reply_to && (
                      <div onClick={() => { const el = document.getElementById(`rrmsg-${msg.reply_to.id}`); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }} style={{ background: dark ? "#ffffff12" : "#00000010", borderLeft: "3px solid #6c5ce7", margin: "8px 8px 0", borderRadius: 8, padding: "5px 8px", cursor: "pointer" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#6c5ce7", marginBottom: 2 }}>{msg.reply_to.alias}</div>
                        <div style={{ fontSize: 11, color: faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{msg.reply_to.type === "image" ? "📷 Photo" : msg.reply_to.content}</div>
                      </div>
                    )}
                    <img src={msg.image_url} alt="shared" style={{ width: "100%", display: "block" }} />
                    {msg.content && <div style={{ padding: "6px 10px", fontSize: 12, color: text }}>{msg.content}</div>}
                  </div>
                ) : (
                  <div style={{ background: isMine ? "linear-gradient(135deg,#6c5ce730,#e91e8c20)" : card, border: `1px solid ${isMine ? "#6c5ce740" : border}`, borderRadius: isMine ? "18px 18px 4px 18px" : "0 18px 18px 18px", padding: "10px 14px", maxWidth: "80%", fontSize: 13, color: text, lineHeight: 1.5 }}>
                    {msg.reply_to && (
                      <div onClick={() => {
                        const el = document.getElementById(`rrmsg-${msg.reply_to.id}`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                      }} style={{ background: dark ? "#ffffff12" : "#00000010", borderLeft: "3px solid #6c5ce7", borderRadius: 8, padding: "5px 8px", marginBottom: 6, cursor: "pointer" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#6c5ce7", marginBottom: 2 }}>{msg.reply_to.alias}</div>
                        {msg.reply_to.type === "image" && msg.reply_to.image_url
                          ? <div style={{ fontSize: 10, color: faint }}>📷 Photo</div>
                          : <div style={{ fontSize: 11, color: faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>{msg.reply_to.content}</div>
                        }
                      </div>
                    )}
                    {msg.content}
                    <div style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", marginTop: 6, paddingTop: 4, borderTop: `1px solid ${isMine ? "#6c5ce720" : border}` }}>
                      <button onClick={() => setReplyingTo({ id: msg.id, alias: msg.alias, content: msg.content, type: msg.type, image_url: msg.image_url })} style={{ background: "none", border: "none", fontSize: 11, cursor: "pointer", color: isMine ? "#a78bfa" : "#6c5ce7", display: "flex", alignItems: "center", gap: 3, padding: 0 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
                        Reply
                      </button>
                    </div>
                  </div>
                )}
                {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                  <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap", justifyContent: isMine ? "flex-end" : "flex-start" }}>
                    {Object.entries(msg.reactions).map(([emoji, count]) => (
                      <div key={emoji} style={{ background: dark ? "#ffffff10" : "#f0f0f0", borderRadius: 20, padding: "2px 8px", fontSize: 11, color: sub }}>{emoji} {count}</div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 4, marginTop: 4, alignItems: "center" }}>
                  {["🔥", "❤️", "😂", "😮"].map(e => (
                    <button key={e} onClick={() => handleReact(msg.id, e)} style={{ background: "none", border: "none", fontSize: 14, cursor: "pointer", opacity: 0.6 }}>{e}</button>
                  ))}
                  {isMine && (
                    <button onClick={async () => {
                      if (!window.confirm("Delete this message?")) return;
                      const r = await api(`/red-rooms/${activeRoom.id}/messages/${msg.id}`, { method: "DELETE" });
                      if (r.ok) setMessages(prev => prev.filter(m => m.id !== msg.id));
                      else flash("Failed to delete", "error");
                    }} style={{ background: "none", border: "none", fontSize: 12, cursor: "pointer", opacity: 0.5, color: "#ef4444", marginLeft: 4 }}>🗑</button>
                  )}
                </div>
                <div style={{ fontSize: 9, color: faint, marginTop: 2 }}>{new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            );
          })}
          {typing.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
              <div style={{ display: "flex", gap: 3 }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ width: 6, height: 6, borderRadius: 3, background: "#6c5ce7", animation: `rr_bounce 1.2s ${i*0.2}s infinite` }} />
                ))}
              </div>
              <span style={{ fontSize: 11, color: "#888" }}>
                {typing.slice(0,2).join(", ")} {typing.length === 1 ? "is" : "are"} typing...
              </span>
              <style>{`@keyframes rr_bounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-4px)}}`}</style>
            </div>
          )}
          {showTip && (
            <div style={{ textAlign: "center", margin: "8px 0", padding: "8px 16px", background: dark ? "#6c5ce720" : "#f0edff", borderRadius: 12, border: "1px solid #6c5ce730", fontSize: 11, color: "#6c5ce7", fontWeight: 600 }}>
              💡 Tap ↩ Reply inside any message bubble to reply to it!
            </div>
          )}
          <div ref={msgEndRef} />
        </div>

        {/* Scroll to bottom button */}
        {showScrollBtn && (
          <div style={{ position: "absolute", bottom: imagePreview ? 180 : 120, right: 16, zIndex: 100, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            {unreadCount > 0 && (
              <div onClick={scrollToBottom} style={{ background: dark ? "#1a1a2a" : "#fff", border: `1px solid ${border}`, borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 700, color: "#6c5ce7", cursor: "pointer", boxShadow: "0 4px 12px rgba(0,0,0,0.3)", whiteSpace: "nowrap" }}>
                {unreadCount} new message{unreadCount > 1 ? "s" : ""} ↓
              </div>
            )}
            <button onClick={scrollToBottom} style={{ width: 44, height: 44, borderRadius: 22, background: dark ? "#1a1a2a" : "#fff", border: `1px solid ${border}`, boxShadow: "0 4px 12px rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6c5ce7" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              {unreadCount > 0 && (
                <div style={{ position: "absolute", top: -4, right: -4, background: "#e53e3e", color: "#fff", fontSize: 9, fontWeight: 800, minWidth: 16, height: 16, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
                  {unreadCount > 9 ? "9+" : unreadCount}
                </div>
              )}
            </button>
          </div>
        )}

        {/* Trivia Challenge Selector */}
        {showTriviaChallenge && (
          <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center", pointerEvents: "none" }}>
            <div style={{ width: "100%", maxWidth: 480, background: dark ? "#161720" : "#fff", borderRadius: "24px 24px 0 0", padding: "20px 20px 40px", pointerEvents: "all", boxShadow: "0 -8px 40px rgba(0,0,0,0.5)" }}>
              <div style={{ width: 36, height: 3, borderRadius: 2, background: "rgba(128,128,128,0.3)", margin: "0 auto 20px" }} />
              <div style={{ fontSize: 16, fontWeight: 800, color: dark ? "#f0f0fa" : "#111", marginBottom: 4 }}>🎮 Trivia Duo Challenge</div>
              <div style={{ fontSize: 12, color: dark ? "#8890b0" : "#6b7094", marginBottom: 20 }}>Beat a room member in 5 rounds of trivia. Winner takes all!</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: dark ? "#8890b0" : "#6b7094", marginBottom: 10 }}>SELECT BET AMOUNT</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {[50, 100, 250, 500].map(amt => (
                  <button key={amt} onClick={() => setTriviaBet(amt)} style={{ flex: 1, padding: "10px 0", borderRadius: 12, border: `${triviaBet === amt ? "2px" : "1px"} solid ${triviaBet === amt ? "#6c5ce7" : border}`, background: triviaBet === amt ? "#6c5ce720" : "transparent", color: triviaBet === amt ? "#6c5ce7" : dark ? "#8890b0" : "#6b7094", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {amt}
                  </button>
                ))}
              </div>
              <div style={{ background: dark ? "#1a1a2a" : "#f4f4f8", borderRadius: 12, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: dark ? "#8890b0" : "#6b7094" }}>
                Your GT balance: <strong style={{ color: "#f59e0b" }}>{(freshGtBalance !== null ? freshGtBalance : Number(user?.gtBalance || 0)).toFixed(0)} GT</strong> · Winner gets <strong style={{ color: "#10b981" }}>{triviaBet * 2} GT</strong>
              </div>
              {freshGtBalance !== null && freshGtBalance < triviaBet && (
                <div style={{ background: "#ef444415", border: "1px solid #ef444430", borderRadius: 12, padding: "10px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", marginBottom: 6 }}>⚠️ Insufficient GT balance!</div>
                  <div style={{ fontSize: 11, color: "#ef444490", marginBottom: 10 }}>You need {triviaBet} GT to place this bet. Get more GT tokens to play.</div>
                  <button onClick={() => { setShowTriviaChallenge(false); go("wallet"); }} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#f59e0b", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    🪙 Get GT Tokens
                  </button>
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setShowTriviaChallenge(false)} style={{ flex: 1, padding: "13px", borderRadius: 12, border: `1px solid ${border}`, background: "transparent", color: dark ? "#8890b0" : "#6b7094", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                <button onClick={async () => {
                  if (!myAlias) { flash("Join a room first", "error"); return; }
                  const r = await api("/trivia/challenge", { method: "POST", body: JSON.stringify({ room_id: activeRoom.id, bet_amount: triviaBet, alias: myAlias.name }) });
                  if (r.ok) { setShowTriviaChallenge(false); flash("Challenge posted! Waiting for opponent... 🎮"); loadMessages(activeRoom.id); }
                  else flash(r.message || "Failed to post challenge", "error");
                }} disabled={freshGtBalance === null || freshGtBalance < triviaBet} style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: (freshGtBalance !== null && freshGtBalance >= triviaBet) ? "linear-gradient(135deg,#6c5ce7,#e91e8c)" : "#6c5ce750", color: "#fff", fontSize: 13, fontWeight: 800, cursor: (freshGtBalance !== null && freshGtBalance >= triviaBet) ? "pointer" : "not-allowed" }}>
                  🎮 Post Challenge · {triviaBet} GT
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trivia Game Modal */}
        {showGameModal && activeGame && (
          <TriviaGameModal
            game={activeGame}
            dark={dark}
            flash={flash}
            myAlias={myAlias?.name}
            onClose={() => setShowGameModal(false)}
            onGameEnd={(finalGame) => { setShowGameModal(false); setActiveGame(null); setGameOver(finalGame); }}
          />
        )}

        {/* Trivia Game Over */}
        {gameOver && (
          <TriviaGameOver
            game={gameOver}
            dark={dark}
            onClose={() => { setGameOver(null); loadMessages(activeRoom.id); }}
          />
        )}

        {/* Reply bar */}
        {replyingTo && (
          <div style={{ padding: "8px 16px", background: dark ? "#111120" : "#f0edff", borderTop: `1px solid #6c5ce730`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <div style={{ flex: 1, borderLeft: "3px solid #6c5ce7", paddingLeft: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6c5ce7" }}>Replying to {replyingTo.alias}</div>
              {replyingTo.type === "image"
                ? <div style={{ fontSize: 11, color: faint }}>📷 Photo</div>
                : <div style={{ fontSize: 11, color: faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{replyingTo.content}</div>
              }
            </div>
            <button onClick={() => setReplyingTo(null)} style={{ background: "none", border: "none", color: "#e53e3e", fontSize: 20, cursor: "pointer", flexShrink: 0 }}>✕</button>
          </div>
        )}

        {/* Image preview */}
        {imagePreview && (
          <div style={{ padding: "8px 16px", background: dark ? "#111120" : "#f8f8ff", borderTop: `1px solid ${border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <img src={imagePreview} alt="preview" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 10 }} />
            <div style={{ flex: 1, fontSize: 12, color: sub }}>Photo ready to send</div>
            <button onClick={() => { setImageFile(null); setImagePreview(null); }} style={{ background: "none", border: "none", color: "#e53e3e", fontSize: 20, cursor: "pointer" }}>✕</button>
          </div>
        )}

        {/* Input bar */}
        <div style={{ background: dark ? "#0d0d14" : "#fff", borderTop: `1px solid ${border}`, padding: "10px 16px", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, overflowX: "auto", scrollbarWidth: "none" }}>
            {[
              { icon: "📷", label: "Photo", action: () => fileRef.current?.click() },
              { icon: "🎁", label: "Gift", action: () => flash("Gift feature coming soon!") },
              { icon: "🎮", label: "Game", action: async () => {
                const r = await api("/auth/me");
               if (r.ok) setFreshGtBalance(parseFloat(r.data.user?.gt_balance || r.data.gt_balance || 0));
                setShowTriviaChallenge(true);
              }},
            ].map((opt, i) => (
              <button key={i} onClick={opt.action} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, background: "none", border: "none", cursor: "pointer", flexShrink: 0, padding: "4px 8px", borderRadius: 8 }}>
                <span style={{ fontSize: 18 }}>{opt.icon}</span>
                <span style={{ fontSize: 9, color: faint }}>{opt.label}</span>
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              style={{ flex: 1, background: dark ? "#1a1a2a" : "#f0f1f6", border: `1px solid ${border}`, borderRadius: 22, padding: "10px 14px", fontSize: 13, color: text, outline: "none", fontFamily: "inherit" }}
              placeholder={`Message as ${myAlias?.name || "..."}...`}
              value={msgText}
              onChange={e => { setMsgText(e.target.value); if (e.target.value.trim()) api(`/red-rooms/${activeRoom.id}/typing`, { method: "POST" }); }}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { handleSend(); e.target.blur(); } }}
            />
            <button onClick={() => { handleSend(); if (document.activeElement) document.activeElement.blur(); }} disabled={sending || (!msgText.trim() && !imageFile)}
              style={{ width: 40, height: 40, borderRadius: 20, background: "#e53e3e", border: "none", color: "#fff", fontSize: 16, cursor: "pointer", flexShrink: 0, opacity: (sending || (!msgText.trim() && !imageFile)) ? 0.4 : 1 }}>↑</button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImagePick} />
        </div>
      </div>
    );
  }

  // ── CREATE VIEW ──
  if (view === "create") {
    const selectedConfig = ROOM_TYPES.find(t => t.id === createType);
    const costMap = { chat: 5, truth_dare: 10, hot_takes: 5, role_play: 20, spicy: 30, vibe: 15 };
    const planMap = { chat: "free", truth_dare: "premium", hot_takes: "free", role_play: "elite", spicy: "elite", vibe: "premium" };

    return (
      <Shell title="Create a Room" showBack onBack={() => setView("browse")}>
        <div style={{ padding: "16px 0" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: sub, marginBottom: 8, textTransform: "uppercase" }}>Room Type</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
            {ROOM_TYPES.filter(t => t.id !== "all").map(t => (
              <button key={t.id} onClick={() => setCreateType(t.id)} style={{ padding: "12px 10px", borderRadius: 14, border: `${createType === t.id ? "2px" : "1px"} solid ${createType === t.id ? t.color : border}`, background: createType === t.id ? t.color + "15" : card, cursor: "pointer", textAlign: "left" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{t.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: createType === t.id ? t.color : text }}>{t.label}</div>
                <div style={{ fontSize: 10, color: faint, marginTop: 2 }}>{costMap[t.id]} GT · {PLAN_LABELS[planMap[t.id]]}</div>
              </button>
            ))}
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: sub, marginBottom: 6, textTransform: "uppercase" }}>Room Name</div>
          <input style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${border}`, background: input, color: text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 16 }}
            placeholder="e.g. Late Night Confessions" value={createName} onChange={e => setCreateName(e.target.value)} maxLength={60} />

          <div style={{ fontSize: 11, fontWeight: 700, color: sub, marginBottom: 8, textTransform: "uppercase" }}>Visibility</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[{ id: "public", icon: "🌍", label: "Public" }, { id: "private", icon: "🔒", label: "Private" }].map(v => (
              <button key={v.id} onClick={() => setCreateVisibility(v.id)} style={{ flex: 1, padding: "10px", borderRadius: 12, border: `${createVisibility === v.id ? "2px" : "1px"} solid ${createVisibility === v.id ? "#6c5ce7" : border}`, background: createVisibility === v.id ? "#6c5ce715" : card, cursor: "pointer", color: createVisibility === v.id ? "#6c5ce7" : text, fontSize: 12, fontWeight: 600 }}>
                {v.icon} {v.label}
              </button>
            ))}
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: sub, marginBottom: 8, textTransform: "uppercase" }}>Capacity: {createCapacity} people</div>
          <input type="range" min="2" max="50" value={createCapacity} onChange={e => setCreateCapacity(parseInt(e.target.value))} style={{ width: "100%", marginBottom: 20, accentColor: "#e53e3e" }} />

<div style={{ fontSize: 11, fontWeight: 700, color: sub, marginBottom: 8, textTransform: "uppercase" }}>Room Duration</div>
<div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
  {[
    { hrs: 2, label: "2 hrs" },
    { hrs: 6, label: "6 hrs" },
    { hrs: 12, label: "12 hrs" },
    { hrs: 24, label: "1 day" },
    { hrs: 48, label: "2 days" },
    { hrs: 72, label: "3 days" },
  ].map(d => (
    <button key={d.hrs} onClick={() => setCreateDuration(d.hrs)} style={{ flex: "1 1 calc(33% - 6px)", padding: "9px 4px", borderRadius: 10, border: `${createDuration === d.hrs ? "2px" : "1px"} solid ${createDuration === d.hrs ? "#e53e3e" : border}`, background: createDuration === d.hrs ? "#e53e3e15" : card, cursor: "pointer", color: createDuration === d.hrs ? "#e53e3e" : text, fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
      {d.label}
    </button>
  ))}
</div>

          <div style={{ background: dark ? "#1a0a0a" : "#fff5f5", border: `1px solid #e53e3e20`, borderRadius: 14, padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#e53e3e", marginBottom: 10 }}>Room Summary</div>
            {[
              { label: "Type", value: `${selectedConfig?.icon} ${selectedConfig?.label}` },
              { label: "Entry fee", value: `${costMap[createType]} GT per person` },
              { label: "You earn", value: `${Math.floor(costMap[createType] * 0.2)} GT per join` },
              { label: "Duration", value: createDuration < 24 ? `${createDuration} hrs` : `${createDuration / 24} day${createDuration > 24 ? "s" : ""}` },
              { label: "Required plan", value: PLAN_LABELS[planMap[createType]] },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", paddingBottom: i < 4 ? 6 : 0, marginBottom: i < 4 ? 6 : 0, borderBottom: i < 4 ? `1px solid #e53e3e15` : "none" }}>
                <span style={{ fontSize: 12, color: sub }}>{s.label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: text }}>{s.value}</span>
              </div>
            ))}
          </div>

          <button onClick={handleCreate} disabled={creating || !createName.trim()}
            style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", background: "linear-gradient(135deg,#e53e3e,#c53030)", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: creating ? 0.7 : 1 }}>
            {creating ? "Creating..." : "🔴 Go Live Now"}
          </button>
        </div>
      </Shell>
    );
  }

  // ── BROWSE VIEW ──
  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'DM Sans',sans-serif" }}>
      <Shell title="Red Room">

        {/* Hero Header */}
        <div style={{ background: "linear-gradient(135deg,#1a0010,#0d0020,#1a0505)", borderRadius: 20, padding: "18px 16px", marginBottom: 14, border: "1px solid #e53e3e20", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%", background: "#e53e3e08" }} />
          <div style={{ position: "absolute", bottom: -40, left: -20, width: 100, height: 100, borderRadius: "50%", background: "#e91e8c08" }} />

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#e53e3e20", border: "2px solid #e53e3e40", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🔴</div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 900, color: "#e53e3e", letterSpacing: -0.5 }}>RED ROOM</div>
              <div style={{ fontSize: 10, color: "#ffffff50", letterSpacing: 2 }}>LIVE · ANONYMOUS · EPHEMERAL</div>
            </div>
          </div>

          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 2 }}>
            Jump into the <span style={{ color: "#e53e3e" }}>heat.</span>
          </div>
          <div style={{ fontSize: 12, color: "#ffffff50", lineHeight: 1.6, marginBottom: 14 }}>
            Real people. Real energy. No names. Just vibes.
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <div style={{ flex: 1, background: "#ffffff08", borderRadius: 12, padding: "10px 12px", border: "1px solid #ffffff10", textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>🔥 {rooms.length}</div>
              <div style={{ fontSize: 10, color: "#ffffff50" }}>Rooms Live</div>
            </div>
            <div style={{ flex: 1, background: "#ffffff08", borderRadius: 12, padding: "10px 12px", border: "1px solid #ffffff10", textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>👁 {rooms.reduce((a, r) => a + (r.current_count || 0), 0)}</div>
              <div style={{ fontSize: 10, color: "#ffffff50" }}>People Inside</div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { const r = rooms[Math.floor(Math.random() * rooms.length)]; if (r) handleJoin(r.id); else flash("No rooms live!", "error"); }}
              style={{ flex: 1, padding: "12px 0", borderRadius: 12, border: "none", background: "#e53e3e", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
              Join Random 🔥
            </button>
            <button onClick={() => setView("create")}
              style={{ flex: 1, padding: "12px 0", borderRadius: 12, border: "1px solid #ffffff25", background: "transparent", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              + Create Room
            </button>
          </div>
        </div>

        {/* Token Drop */}
        <TokenDropBanner />

        {/* My hosted rooms */}
        {myRooms.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: sub, marginBottom: 8, textTransform: "uppercase" }}>Your Active Rooms</div>
            {myRooms.map(room => (
              <div key={room.id} onClick={() => handleJoin(room.id)} style={{ background: card, borderRadius: 14, border: `2px solid #6c5ce740`, padding: "14px", marginBottom: 8, cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{room.type_icon} {room.name}</div>
                    <div style={{ fontSize: 11, color: sub }}>Code: <span style={{ color: "#6c5ce7", fontWeight: 800, letterSpacing: 2 }}>{room.code}</span></div>
                  </div>
                  <div style={{ fontSize: 11, color: "#a78bfa", background: "#6c5ce720", padding: "3px 8px", borderRadius: 20, fontWeight: 700 }}>Hosting</div>
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 11, color: faint }}>
                  <span>👥 {room.current_count}/{room.max_capacity}</span>
                  <span>⏱ {timeLeft(room.closes_at)}</span>
                  <span>🪙 +{room.gt_earned_host} GT</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Join by code */}
        <div style={{ background: card, borderRadius: 14, border: `1px solid ${border}`, padding: "14px", marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: text, marginBottom: 10 }}>🔑 Have a room code?</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid ${border}`, background: input, color: text, fontSize: 13, outline: "none", fontFamily: "inherit", letterSpacing: 2, textTransform: "uppercase" }}
              placeholder="ABCD-EFGH" value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && handleJoinByCode()} maxLength={9} />
            <button onClick={handleJoinByCode} style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: "#6c5ce7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Join</button>
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14, overflowX: "auto", scrollbarWidth: "none" }}>
          {ROOM_TYPES.map(t => (
            <button key={t.id} onClick={() => setFilterType(t.id)} style={{ padding: "7px 14px", borderRadius: 20, border: `${filterType === t.id ? "none" : `1px solid ${border}`}`, background: filterType === t.id ? t.color : card, color: filterType === t.id ? "#fff" : sub, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, fontFamily: "inherit" }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

       {/* Live Now */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {[0, 0.6, 1.2].map((delay, i) => (
                <div key={i} style={{ position: "absolute", borderRadius: "50%", width: 24, height: 24, border: "1.5px solid rgba(255,0,0,0.4)", animation: `gcPulse 2s ${delay}s infinite` }} />
              ))}
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: "radial-gradient(circle at 30% 30%,#ff5a5a,#ff0000)", boxShadow: "0 0 8px rgba(255,0,0,0.6)", animation: "gcHeartbeat 1.1s infinite" }} />
              <div style={{ position: "absolute", top: -4, right: -8, background: "#ff0033", color: "white", fontSize: 7, fontWeight: 700, padding: "2px 4px", borderRadius: 999, letterSpacing: ".5px", boxShadow: "0 0 6px rgba(255,0,0,0.7)", animation: "gcBlink 1s infinite", whiteSpace: "nowrap" }}>LIVE</div>
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: dark ? "#f0f0fa" : "#111" }}>Live Now ({rooms.length})</div>
          </div>
        </div> 

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: faint }}>Loading rooms...</div>
        ) : rooms.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 0 20px" }}>
            <div style={{ position: "relative", width: 90, height: 90, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
              {[0, 0.6, 1.2].map((delay, i) => (
                <div key={i} style={{ position: "absolute", borderRadius: "50%", width: 70, height: 70, border: "2px solid rgba(255,0,0,0.4)", animation: `gcPulse 2s ${delay}s infinite` }} />
              ))}
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "radial-gradient(circle at 30% 30%,#ff5a5a,#ff0000)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 0 5px rgba(255,0,0,0.08),0 0 25px rgba(255,0,0,0.6),0 0 45px rgba(255,0,0,0.4)", animation: "gcHeartbeat 1.1s infinite" }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#ff0000", boxShadow: "inset 0 3px 8px rgba(255,255,255,.35),0 0 12px rgba(255,0,0,0.9)" }} />
              </div>
              <div style={{ position: "absolute", top: -6, right: -10, background: "#ff0033", color: "white", fontSize: 10, fontWeight: 700, padding: "4px 7px", borderRadius: 999, letterSpacing: ".5px", boxShadow: "0 0 12px rgba(255,0,0,0.7)", animation: "gcBlink 1s infinite" }}>LIVE</div>
              <style>{`@keyframes gcPulse{0%{transform:scale(.7);opacity:.8;}100%{transform:scale(1.7);opacity:0;}}@keyframes gcHeartbeat{0%{transform:scale(1);}14%{transform:scale(1.12);}28%{transform:scale(1);}42%{transform:scale(1.12);}70%{transform:scale(1);}}@keyframes gcBlink{0%,50%,100%{opacity:1;}25%,75%{opacity:.35;}}`}</style>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: dark ? "#f0f0fa" : "#111", marginBottom: 6 }}>No rooms live right now</div>
            <div style={{ fontSize: 13, color: dark ? "#8890b0" : "#6b7094" }}>Be the first to create one!</div>
          </div>
        ) : (
          rooms.map(room => (
            <div key={room.id} style={{ background: card, borderRadius: 16, border: `1px solid ${border}`, padding: "14px", marginBottom: 10, position: "relative", overflow: "hidden" }}>
              {/* Top color line */}
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${ROOM_TYPES.find(t => t.id === room.type)?.color || "#e53e3e"}60,transparent)` }} />

              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, flexShrink: 0, background: (ROOM_TYPES.find(t => t.id === room.type)?.color || "#e53e3e") + "20", border: `1px solid ${(ROOM_TYPES.find(t => t.id === room.type)?.color || "#e53e3e")}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>
                  {room.type_icon}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <LiveBadge color={ROOM_TYPES.find(t => t.id === room.type)?.color || "#e53e3e"} />
                    <span style={{ fontSize: 10, color: faint }}>{room.type_label}</span>
                  </div>

                  <div style={{ fontSize: 14, fontWeight: 700, color: text, marginBottom: 4 }}>{room.name}</div>
                  <div style={{ fontSize: 11, color: sub, marginBottom: 8 }}>
                    👥 {room.current_count}/{room.max_capacity}
                    {room.closes_at && ` · ⏱ ${timeLeft(room.closes_at)}`}
                  </div>

                  {/* Music wave for vibe rooms */}
                  {room.type === "vibe" && <div style={{ marginBottom: 8 }}><MusicWave color={ROOM_TYPES.find(t => t.id === "vibe").color} /></div>}

                  {/* Plan/verified badges */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                    {room.required_plan !== "free" && (
                      <span style={{ fontSize: 10, color: room.required_plan === "elite" ? "#f59e0b" : "#a78bfa", background: room.required_plan === "elite" ? "#f59e0b15" : "#6c5ce715", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>
                        {PLAN_LABELS[room.required_plan]} required
                      </span>
                    )}
                    {room.requires_verified && (
                      <span style={{ fontSize: 10, color: "#3b82f6", background: "#3b82f615", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>✓ Verified only</span>
                    )}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: faint }}>
                      Stake: <span style={{ color: "#f59e0b", fontWeight: 700 }}>{room.entry_cost_gt} GT</span>
                    </span>
                    <button onClick={() => handleJoin(room.id)} disabled={joiningId === room.id}
                      style={{ padding: "8px 16px", borderRadius: 10, border: "none", background: room.entry_cost_gt === 0 ? "#10b981" : "#e53e3e", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: joiningId === room.id ? 0.7 : 1, fontFamily: "inherit" }}>
                      {joiningId === room.id ? "..." : room.entry_cost_gt === 0 ? "Join Free" : `Join · ${room.entry_cost_gt} GT`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        {/* Explore Room Types */}
        <div style={{ marginTop: 8, marginBottom: 16 }}>
          <ExploreTypes dark={dark} onSelect={(type) => { setCreateType(type); setView("create"); }} />
        </div>

        <div style={{ height: 80 }} />
      </Shell>
    </div>
  );
}